import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { COMPANY, SERVICES } from "@/lib/site";
import { Phone, Mail, MapPin, Facebook, Instagram, ArrowRight, Loader2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "maintenance", label: "Maintenance / Property Mgmt." },
  { value: "other", label: "Other / Not sure" },
];

const INITIAL = { name: "", email: "", phone: "", service_type: "", project_type: "residential", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thanks! We'll reach out within one business day.");
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-20 md:py-32">
      <div className="px-5 sm:px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: Heading + info */}
          <div className="lg:col-span-5">
            <div className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.25em] sm:tracking-[0.3em] text-sunrise">— Start your project</div>
            <h2 className="mt-4 font-display font-black text-navy tracking-tight text-[32px] sm:text-5xl lg:text-6xl leading-[1.04]">
              Free estimate. <br />No pressure.
            </h2>
            <p className="mt-6 text-muted-foreground text-base lg:text-lg leading-relaxed">
              Tell us about your project and we&apos;ll get back to you within one business day with next steps. Prefer to talk?
              Give us a call — we answer.
            </p>

            <div className="mt-10 space-y-5">
              <InfoRow icon={Phone} label="Call" value={COMPANY.phone} href={COMPANY.phoneHref} testid="contact-info-phone" />
              <InfoRow icon={Mail} label="Email" value={COMPANY.email} href={COMPANY.emailHref} testid="contact-info-email" />
              <InfoRow icon={MapPin} label="Service Area" value="Utah · Residential & Commercial" testid="contact-info-area" />
              <InfoRow
                icon={Facebook}
                label="Facebook"
                value="facebook.com/fivepeaksconstructionllc"
                href={COMPANY.facebook}
                testid="contact-info-facebook"
                external
              />
              <InfoRow
                icon={Instagram}
                label="Instagram"
                value="@fivepeaks_llc"
                href={COMPANY.instagram}
                testid="contact-info-instagram"
                external
              />
            </div>
          </div>

          {/* Right: Form */}
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-secondary p-5 sm:p-8 lg:p-12 border border-border rounded-2xl elev-1"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full name *" data-testid="form-field-name">
                <input
                  data-testid="form-input-name"
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="w-full bg-white border border-border px-4 py-3 rounded-lg focus:outline-none focus:border-sunrise focus:ring-1 focus:ring-sunrise transition-colors"
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Phone *">
                <input
                  data-testid="form-input-phone"
                  required
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="w-full bg-white border border-border px-4 py-3 rounded-lg focus:outline-none focus:border-sunrise focus:ring-1 focus:ring-sunrise transition-colors"
                  placeholder="801-555-0144"
                />
              </Field>
              <Field label="Email *" full>
                <input
                  data-testid="form-input-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="w-full bg-white border border-border px-4 py-3 rounded-lg focus:outline-none focus:border-sunrise focus:ring-1 focus:ring-sunrise transition-colors"
                  placeholder="you@example.com"
                />
              </Field>
              <Field label="Service needed *">
                <Select value={form.service_type} onValueChange={(v) => update("service_type", v)}>
                  <SelectTrigger
                    data-testid="form-select-service"
                    className="w-full bg-white border border-border px-4 py-6 rounded-lg focus:ring-sunrise focus:border-sunrise h-auto"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.key} value={s.name}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Project type">
                <Select value={form.project_type} onValueChange={(v) => update("project_type", v)}>
                  <SelectTrigger
                    data-testid="form-select-project-type"
                    className="w-full bg-white border border-border px-4 py-6 rounded-lg focus:ring-sunrise focus:border-sunrise h-auto"
                  >
                    <SelectValue placeholder="Select project type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_TYPES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Tell us about your project *" full>
                <textarea
                  data-testid="form-input-message"
                  required
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={5}
                  className="w-full bg-white border border-border px-4 py-3 rounded-lg focus:outline-none focus:border-sunrise focus:ring-1 focus:ring-sunrise transition-colors resize-none"
                  placeholder="Scope, location, timeline, budget range — whatever helps us help you."
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="form-submit"
              className="mt-8 w-full md:w-auto bg-sunrise hover:bg-sunrise-hover text-white px-10 py-5 text-sm font-bold tracking-wider uppercase inline-flex items-center justify-center gap-3 transition-colors disabled:opacity-60 rounded-xl shadow-lg shadow-sunrise/20"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Request a Free Quote <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
            <p className="mt-4 text-xs text-muted-foreground">
              By submitting this form you agree we can contact you about your project. We never share your information.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="block text-xs font-bold uppercase tracking-[0.2em] text-navy mb-2">{label}</span>
      {children}
    </label>
  );
}

function InfoRow({ icon: Icon, label, value, href, testid, external }) {
  const Wrap = href ? "a" : "div";
  const props = href ? { href, ...(external ? { target: "_blank", rel: "noopener noreferrer" } : {}) } : {};
  return (
    <Wrap data-testid={testid} {...props} className="flex items-start gap-4 group">
      <span className="w-12 h-12 grid place-items-center bg-secondary rounded-xl text-navy group-hover:bg-sunrise/15 group-hover:text-sunrise transition-colors">
        <Icon className="w-5 h-5" />
      </span>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <div className="font-display font-bold text-navy text-lg group-hover:text-sunrise transition-colors">{value}</div>
      </div>
    </Wrap>
  );
}
